import type React from 'react';
import dynamic from 'next/dynamic';

// Interfaces
import type { AuthorPageData } from 'interfaces/content/AuthorPageData';
import type { PageInfo } from 'interfaces/components/PageInfo';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import { SectionVariant } from 'interfaces/Section';

// Constants
import { UIFragments } from 'utils/log/constants/uiFragments';

// Utils
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';

// Components
import { RightRailSimple } from 'components/Ad/AdSlot';
import ErrorBoundary from 'components/ErrorBoundary';
import Pagination from 'components/Pagination/Pagination';

import * as S from './AuthorStyles';

const AuthorHero = dynamic(() => import('components/AuthorHero'));

const AuthorArticles = dynamic(() => import('components/recommends/ArticleSection/AuthorArticles'));

interface AuthorProps {
  author: AuthorPageData;
  allPostsArray: ArticlePreview[];
  authorPageInfo: PageInfo;
  pageNum: string;
  name: string;
  pageSize: number;
  pagination?: PagePagination;
}

const AuthorPage: React.FC<AuthorProps> = ({ author, allPostsArray, pagination }) => {
  const authorPosts = allPostsArray;

  const hasPosts = allPostsArray.length > 0;

  return (
    <S.AuthorGridLayout>
      <ErrorBoundary fragment={UIFragments.AUTHOR_PAGE}>
        <AuthorHero
          avatarUrl={author.avatarUrl}
          description={author.description}
          displayEmail={author.displayEmail}
          displayName={author.displayName}
          instaHandle={author.instaHandle}
          instaUrl={author.instaUrl}
          linkedinHandle={author.linkedinHandle}
          linkedinUrl={author.linkedinUrl}
          twitterHandle={author.twitterHandle}
          twitterUrl={author.twitterUrl}
        />
      </ErrorBoundary>
      {pagination && pagination.showPageInfo && (
        <ErrorBoundary fragment={UIFragments.AUTHOR_PAGE}>
          <S.PageInfo
            className='author'
            as='h4'
            $size={{ default: 'level7', mdMin: 'level6' }}
            $fontFamily='graphikCond'
          >
            {pagination.pageInfo}
          </S.PageInfo>
        </ErrorBoundary>
      )}
      {hasPosts ? (
        <div className='articles'>
          <ErrorBoundary fragment={UIFragments.AUTHOR_PAGE}>
            <AuthorArticles
              pagination={pagination}
              variant={SectionVariant.AuthorArticles}
              articles={authorPosts}
            />
          </ErrorBoundary>
          <Pagination pagination={pagination} />
        </div>
      ) : (
        <S.NoArticles>No articles</S.NoArticles>
      )}
      <S.AuthorSideBar className='sidebar'>
        <RightRailSimple
          className='side-ad'
          id={0}
        />
      </S.AuthorSideBar>
    </S.AuthorGridLayout>
  );
};

export default AuthorPage;
